import { useMemo, useState } from "react";

import { useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { useCategoryAttributes, useUpdateProduct } from "@/hooks/use-catalog";
import type { Product } from "@/lib/catalog/types";

type AttrValueMap = Record<string, string>;

function readAttrValues(
	attributes: Record<string, unknown> | null | undefined,
): AttrValueMap {
	const raw = (attributes as { attrValues?: unknown } | null | undefined)
		?.attrValues;
	if (!raw || typeof raw !== "object") return {};
	const out: AttrValueMap = {};
	for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
		if (v !== null && v !== undefined) out[k] = String(v);
	}
	return out;
}

/**
 * Carte « Caractéristiques (catégorie) » du wizard (T09-9.1) : propose
 * automatiquement les champs définis pour la catégorie du produit et persiste
 * les valeurs dans product.attributes.attrValues.
 */
export function CategoryAttributesCard({ product }: { product: Product }) {
	const { data: attrs, isLoading } = useCategoryAttributes(product.categoryId);
	const updateMut = useUpdateProduct(product.id);
	const toast = useToast();
	const initial = useMemo(
		() => readAttrValues(product.attributes),
		[product.attributes],
	);
	const [values, setValues] = useState<AttrValueMap>(initial);
	const dirty = useMemo(
		() => JSON.stringify(values) !== JSON.stringify(initial),
		[values, initial],
	);

	const set = (code: string, v: string) =>
		setValues((prev) => {
			const next = { ...prev };
			if (v === "") delete next[code];
			else next[code] = v;
			return next;
		});

	const save = async () => {
		try {
			await updateMut.mutateAsync({
				attributes: { ...product.attributes, attrValues: values },
			});
			toast.success("Caractéristiques enregistrées.");
		} catch (e) {
			toast.error(extractErrorMessage(e, "Enregistrement impossible"));
		}
	};

	return (
		<div className="card">
			<div className="card-h">
				<div>
					<h3>Caractéristiques de la catégorie</h3>
					<p className="sub muted">
						Champs proposés automatiquement selon la catégorie du produit
					</p>
				</div>
				{dirty && (
					<button
						className="btn btn-primary"
						onClick={() => void save()}
						disabled={updateMut.isPending}
					>
						{updateMut.isPending ? "Enregistrement…" : "Enregistrer"}
					</button>
				)}
			</div>
			<div className="card-b">
				{isLoading && <p className="muted">Chargement…</p>}
				{!isLoading && (!attrs || attrs.length === 0) && (
					<p className="muted" style={{ fontSize: 13 }}>
						Aucune caractéristique définie pour cette catégorie. Un responsable
						peut en définir dans <strong>Catalogue → Attributs</strong>.
					</p>
				)}
				{!isLoading && attrs && attrs.length > 0 && (
					<div
						style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
					>
						{attrs.map(({ attribute, isRequired }) => {
							const v = values[attribute.code] ?? "";
							const label = (
								<span
									style={{
										display: "block",
										fontSize: 12,
										fontWeight: 600,
										marginBottom: 4,
									}}
								>
									{attribute.label}
									{attribute.unit ? ` (${attribute.unit})` : ""}
									{isRequired && (
										<span style={{ color: "var(--a-danger, #c00)" }}> *</span>
									)}
								</span>
							);
							return (
								<div key={attribute.id}>
									{label}
									{attribute.type === "select" && (
										<select
											className="aform-i"
											style={{ width: "100%" }}
											value={v}
											onChange={(e) => set(attribute.code, e.target.value)}
										>
											<option value="">— Non renseigné —</option>
											{attribute.values.map((av) => (
												<option key={av.id} value={av.value}>
													{av.value}
												</option>
											))}
										</select>
									)}
									{attribute.type === "text" && (
										<input
											className="aform-i"
											style={{ width: "100%" }}
											value={v}
											onChange={(e) => set(attribute.code, e.target.value)}
										/>
									)}
									{attribute.type === "number" && (
										<input
											type="number"
											className="aform-i"
											style={{ width: "100%" }}
											value={v}
											onChange={(e) => set(attribute.code, e.target.value)}
										/>
									)}
									{attribute.type === "boolean" && (
										<select
											className="aform-i"
											style={{ width: "100%" }}
											value={v}
											onChange={(e) => set(attribute.code, e.target.value)}
										>
											<option value="">— Non renseigné —</option>
											<option value="true">Oui</option>
											<option value="false">Non</option>
										</select>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
